import { BlockDetails } from "@/components/people/dialogs/BlockDetailsDialog"
import { ShiftDetails } from "@/components/sheriff/shift/ShiftDetailsDialog"
import { INVALID_UK_TIME_ERROR, ukWallClockToInstant } from "@/constants/sheriffOptions"
import { ukInstantOf } from "@/lib/ukCalendarDate"
import { UpdateShiftBody } from "@/services/shiftsService"

function parseShiftDateTime(date: Date | undefined, time: string | undefined) {
  if (!date || !time) {
    throw new Error("Invalid date or time")
  }

  const instant = ukInstantOf(date, time)

  if (Number.isNaN(instant.getTime())) {
    throw new Error(INVALID_UK_TIME_ERROR)
  }

  return instant
}

export function buildShiftSaveDiff(
  updated: ShiftDetails,
  baseline: ShiftDetails,
): {
  personChanged: boolean
  shouldCancelShift: boolean
  hasChanges: boolean
  onlyNotesChanged: boolean
  updateBody: UpdateShiftBody
} {
  const personChanged = (updated.personId ?? "") !== (baseline.personId ?? "")
  const notesChanged = (updated.notes ?? "") !== (baseline.notes ?? "")
  const startDateTimeChanged =
    updated.startDate?.toDateString() !== baseline.startDate?.toDateString() ||
    (updated.startTime ?? "") !== (baseline.startTime ?? "")
  const endDateTimeChanged =
    updated.endDate?.toDateString() !== baseline.endDate?.toDateString() ||
    (updated.endTime ?? "") !== (baseline.endTime ?? "")
  const cancelledReasonChanged =
    (updated.cancelledReason ?? "") !== (baseline.cancelledReason ?? "")
  const shouldCancelShift =
    Boolean(updated.cancelledReason) && updated.status !== "cancelled" && cancelledReasonChanged
  const onlyNotesChanged =
    notesChanged &&
    !personChanged &&
    !startDateTimeChanged &&
    !endDateTimeChanged &&
    !cancelledReasonChanged

  const updateBody: UpdateShiftBody = { id: updated.id }

  if (startDateTimeChanged) {
    const startDateTime = parseShiftDateTime(updated.startDate, updated.startTime)
    updateBody.startTime = startDateTime.toISOString()
  }

  if (endDateTimeChanged) {
    const endDateTime = parseShiftDateTime(updated.endDate, updated.endTime)
    updateBody.endTime = endDateTime.toISOString()
  }

  if (notesChanged) {
    updateBody.notes = updated.notes || null
  }

  const hasChanges =
    personChanged || notesChanged || startDateTimeChanged || endDateTimeChanged || shouldCancelShift

  return { personChanged, shouldCancelShift, hasChanges, onlyNotesChanged, updateBody }
}

export function buildBlockDetailsDiff(
  updated: BlockDetails,
  baseline: BlockDetails,
): {
  hasChanges: boolean
  updateBody: UpdateShiftBody
} {
  const updateBody: UpdateShiftBody = { id: updated.id }

  const hasDateTimeChanged =
    updated.startDate !== baseline.startDate ||
    updated.endDate !== baseline.endDate ||
    (updated.startTime ?? "") !== (baseline.startTime ?? "") ||
    (updated.endTime ?? "") !== (baseline.endTime ?? "")

  if (hasDateTimeChanged) {
    const startDateTime = ukWallClockToInstant(updated.startDate, updated.startTime)
    const endDateTime = ukWallClockToInstant(updated.endDate, updated.endTime)

    if (isNaN(startDateTime.getTime()) || isNaN(endDateTime.getTime())) {
      throw new Error(INVALID_UK_TIME_ERROR)
    }

    if (startDateTime > endDateTime) {
      throw new Error("End date/time must be after start date/time")
    }

    updateBody.startTime = startDateTime.toISOString()
    updateBody.endTime = endDateTime.toISOString()
  }

  if (updated.area !== baseline.area) updateBody.area = updated.area
  if (updated.type !== baseline.type) updateBody.type = updated.type
  if (updated.role !== baseline.role) updateBody.employer = updated.role
  if (updated.location !== baseline.location) updateBody.location = updated.location
  if (updated.notes !== baseline.notes) updateBody.notes = updated.notes || null

  const hasChanges = Object.keys(updateBody).length > 1 // more than just id

  return { hasChanges, updateBody }
}
